// Redux slice
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useAppSelector } from "../store";
import type { RootState } from "../store";

export interface StakingState {
  isLoading: boolean;
  error: string | null;
  totalStaked: number;
  stakeModalOpen: boolean;
  unstakeModalOpen: boolean;
  exportedSummarySnackbarOpen: boolean;
  stakeStage: "info" | "stake" | "review" | "submitted";
  unstakeStage: "info" | "unstake" | "review" | "submitted";
  twoFAModalOpen: boolean;
}

const initialState: StakingState = {
  isLoading: false,
  error: null,
  totalStaked: 0,
  stakeModalOpen: false,
  unstakeModalOpen: false,
  exportedSummarySnackbarOpen: false,
  stakeStage: "info",
  unstakeStage: "info",
  twoFAModalOpen: false,
};

const StakingSlice = createSlice({
  name: "staking",
  initialState,
  reducers: {
    setIsLoading: (state: StakingState, action: PayloadAction<boolean>): void => {
      state.isLoading = action.payload;
    },
    setError: (state: StakingState, action: PayloadAction<string | null>): void => {
      state.error = action.payload;
    },
    setTotalStaked: (state: StakingState, action: PayloadAction<number>): void => {
      state.totalStaked = action.payload;
    },
    setStakeModalOpen: (state: StakingState, action: PayloadAction<boolean>): void => {
      state.stakeModalOpen = action.payload;
    },
    setUnstakeModalOpen: (state: StakingState, action: PayloadAction<boolean>): void => {
      state.unstakeModalOpen = action.payload;
    },
    setExportedSummarySnackbarOpen: (state: StakingState, action: PayloadAction<boolean>): void => {
      state.exportedSummarySnackbarOpen = action.payload;
    },
    setStakeStage: (
      state: StakingState,
      action: PayloadAction<"info" | "stake" | "review" | "submitted">,
    ): void => {
      state.stakeStage = action.payload;
    },
    setUnstakeStage: (
      state: StakingState,
      action: PayloadAction<"info" | "unstake" | "review" | "submitted">,
    ): void => {
      state.unstakeStage = action.payload;
    },
    setTwoFAModalOpen: (state: StakingState, action: PayloadAction<boolean>): void => {
      state.twoFAModalOpen = action.payload;
    },
    resetStakingState: () => initialState,
  },
});

export const {
  setIsLoading,
  setError,
  setTotalStaked,
  resetStakingState,
  setStakeModalOpen,
  setUnstakeModalOpen,
  setExportedSummarySnackbarOpen,
  setStakeStage,
  setUnstakeStage,
  setTwoFAModalOpen,
} = StakingSlice.actions;

export const useStakingState = () => useAppSelector((state: RootState) => state.staking);
export const useStakingSliceActions = () => StakingSlice.actions;

export default StakingSlice;
